// src/components/PortfolioReadinessCard.jsx
// Portfolio Readiness (§9.3) — dashboard Family Office.
// Agrège la Collection Readiness par famille (même moteur commun, §9.5) sans
// mélanger les données : lecture via artworks_secure (RLS/mandat). i18n FR / EN / DE.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { computePortfolioReadiness, readinessPriority, READINESS_SELECT_SECURE } from "../utils/readiness";

const fmtEUR = (n, locale) =>
  (Number(n) || 0).toLocaleString(locale || undefined, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });

const tone = (pct) => (pct >= 80 ? "text-emerald-700" : pct >= 60 ? "text-amber-700" : "text-rose-700");
const prioBadge = { high: "bg-rose-100 text-rose-700", medium: "bg-amber-100 text-amber-700", low: "bg-emerald-100 text-emerald-700" };

export default function PortfolioReadinessCard({ clients = [] }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || undefined;
  const [byClient, setByClient] = useState(null);
  const [overall, setOverall] = useState(0);
  const [loading, setLoading] = useState(true);

  const clientIds = useMemo(
    () => [...new Set((clients || []).map((c) => c.id).filter(Boolean))],
    [clients]
  );

  useEffect(() => {
    let alive = true;
    if (clientIds.length === 0) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("artworks_secure")
        .select(READINESS_SELECT_SECURE)
        .in("client_id", clientIds);
      if (!alive) return;
      if (error) { console.error("[PortfolioReadinessCard]", error); setByClient(null); }
      else {
        const p = computePortfolioReadiness(data || []);
        setByClient(p.perClient);
        setOverall(p.overall);
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [clientIds]);

  if (loading || !byClient) return null;

  // Lignes = familles connues, triées par priorité (readiness croissant).
  const rows = (clients || [])
    .map((c) => {
      const r = byClient[c.id];
      const pct = r ? r.overall : 0;
      return { id: c.id, name: c.name || "—", value: c.totalValue || 0, pct, hasData: !!r, prio: readinessPriority(pct) };
    })
    .sort((a, b) => a.pct - b.pct);

  if (rows.length === 0) return null;

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">{t("readiness.cards.portfolio.eyebrow")}</p>
          <h2 className="mt-1 font-serif text-xl font-semibold text-gray-950" style={{ fontFamily: "Georgia, serif" }}>
            {t("readiness.cards.portfolio.title")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">{t("readiness.cards.portfolio.subtitle")}</p>
        </div>
        <div className="text-right">
          <div className={`text-4xl font-semibold ${tone(overall)}`}>{overall}%</div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400">{t("readiness.cards.portfolio.consolidated")}</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-400">
              <th className="py-2 pr-3">{t("readiness.cards.portfolio.columns.family")}</th>
              <th className="py-2 px-3 text-right">{t("readiness.cards.portfolio.columns.artValue")}</th>
              <th className="py-2 px-3 text-right">{t("readiness.cards.portfolio.columns.readiness")}</th>
              <th className="py-2 pl-3">{t("readiness.cards.portfolio.columns.priority")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-gray-100 last:border-0">
                <td className="py-2.5 pr-3 font-medium text-gray-900">{r.name}</td>
                <td className="py-2.5 px-3 text-right text-gray-700">{fmtEUR(r.value, locale)}</td>
                <td className={`py-2.5 px-3 text-right font-semibold ${tone(r.pct)}`}>
                  {r.hasData ? `${r.pct}%` : "—"}
                </td>
                <td className="py-2.5 pl-3">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${prioBadge[r.prio.key]}`}>
                    {t(`readiness.priority.${r.prio.key}`)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
